<template>
  <div>
    <div class="row">
      <div class="col-lg-6 mx-auto">
        <div class="card">
          <div class="card-body">
            <b-modal v-model="joinOrganizationModal" :title="`Request to Join ${existingOrganizationName}`" hide-footer>
              <form ref="joinForm" @submit.stop.prevent="handleJoinSubmit">
                <div class="row">
                  <div class="col-lg-6">
                    <b-form-group label="First Name">
                      <b-form-input v-model="firstName" required/>
                    </b-form-group>
                  </div>

                  <div class="col-lg-6">
                    <b-form-group label="Last Name">
                      <b-form-input v-model="lastName" required/>
                    </b-form-group>
                  </div>
                </div>

                <b-form-group label="Email Address">
                  <b-form-input type="email" v-model="email" required/>
                </b-form-group>

                <div class="d-flex gap-1 justify-content-end">
                  <b-button @click="joinOrganizationModal = false">Cancel</b-button>
                  <b-button type="submit" variant="primary">Request to Join</b-button>
                </div>
              </form>

            </b-modal>

            <form v-if="!didFinalizeForm" ref="createForm" @submit.stop.prevent="handleCreateSubmit">

              <h6 class="font-weight-semi-bold">Agency Account Setup Information</h6>
              <p class="text-gray-600 font-size-sm" v-if="holdingCompanies">
                <b>PLEASE READ</b> - This sign up form is only for agencies under the <b>Omnicom</b> or <b>Interpublic
                Group</b> holding companies.
                Other agencies, <a href="#" @click="handleIntercom">click here</a> to request access
              </p>

              <b-form-group label="Holding Company">
                <b-form-select :options="holdingCompaniesOptions" v-model="holdingCompany" required/>
              </b-form-group>

              <b-form-group label="Agency Name">
                <b-form-input v-model="organizationName" debounce="500" required :state="agencyError ? false : null"/>
              </b-form-group>

              <div class="text-center" v-if="agencyError">This agency already has an account,
                <b-button class="p-0" variant="link" @click="joinOrganizationModal = true">Request to Join</b-button>
              </div>

              <h6 class="font-weight-semi-bold mt-3">Account Administrator</h6>
              <p class="text-gray-600 font-size-sm">The account admin will manage the account on behalf of your
                agency</p>

              <div class="row">
                <div class="col-lg-6">
                  <b-form-group label="First Name">
                    <b-form-input v-model="firstName" required/>
                  </b-form-group>
                </div>

                <div class="col-lg-6">
                  <b-form-group label="Last Name">
                    <b-form-input v-model="lastName" required/>
                  </b-form-group>
                </div>
              </div>

              <b-form-group label="Email Address">
                <b-form-input v-model="email" required debounce="500"/>
              </b-form-group>

              <div class="text-center mb-3" v-if="emailError">You already have an account,
                <a href="/users/sign-in">Log In Now</a>
              </div>

              <b-button type="submit" variant="primary" block :disabled="!validForCreate">Sign Up</b-button>
            </form>

            <div class="px-3 pb-3 d-flex flex-column align-items-center" v-if="didFinalizeForm">
              <i class="far fa-3x fa-check-circle text-success m-2"></i>

              <div class="d-flex flex-column" v-if="didJoin">
                <h4 class="text-center font-weight-bold">Your request to join {{ existingOrganizationName }} was sent to the agency’s account administrator</h4>
                <p class="text-gray-500 text-center">Please check your email for a response.</p>
              </div>

              <div class="d-flex flex-column" v-if="didSignUp">
                <h4 class="text-center font-weight-bold">Your request to create an account for {{ organizationName }} is being reviewed.</h4>
                <p class="text-gray-500 text-center">Please allow up to 72 hours for a response.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import normalize from "json-api-normalize";

import GeneralMixins from "../../../mixins/GeneralMixins.js";
import Attributes from "../../../models/Attributes.js";

export default {
  data() {
    return {
      holdingCompanies: null,
      loading: false,
      organizationName: null,
      holdingCompany: null,
      firstName: null,
      lastName: null,
      email: null,
      agencyError: null,
      joinOrganizationModal: false,
      existingOrganization: null,
      emailError: false,
      didSignUp: false,
      didJoin: false
    }
  },

  mixins: [GeneralMixins],

  watch: {
    organizationName(val) {
      if (val) {
        this.checkAgency()
      }
    },

    email(val) {
      if (val) {
        this.checkEmail()
      }
    }
  },

  mounted() {
    this.loadHoldingCompanies()
  },

  methods: {

    handleIntercom() {
      if ('undefined' !== typeof Intercom) {
        Intercom('show')
      }
    },

    handleJoinSubmit() {
      if (!this.$refs.joinForm.checkValidity()) {
        return
      }

      this.$http.post(`/agencies/organizations/${this.existingOrganization.id}/public_requests`, {
        user: {
          first_name: this.firstName,
          last_name: this.lastName,
          email: this.email
        }
      }).then(success => {
        this.joinOrganizationModal = false
        this.didJoin = true
      }, failure => {
        this.showErrorToast(null, failure.body.error)
      })
    },

    handleCreateSubmit() {
      if (!this.$refs.createForm.checkValidity()) {
        return
      }

      const params = {
        organization: {
          name: this.organizationName,
          holding_company_id: this.holdingCompany,
          owner_attributes: {
            first_name: this.firstName,
            last_name: this.lastName,
            email: this.email
          }
        }
      }

      this.$http.post("/agencies/registrations", params).then(success => {
        this.didSignUp = true
      }, failure => {
        this.showErrorToast(null, failure.body.error)
      })
    },

    loadHoldingCompanies() {
      if (this.loading || this.holdingCompanies) {
        return
      }

      this.loading = true
      this.$http.get("/agencies/holding_companies").then(result => {
        this.holdingCompanies = normalize(result.data).get(Attributes.holdingCompanyAttributes)
        this.loading = false
      }, failure => {
        this.loading = false
      })
    },

    checkAgency() {
      this.agencyError = null
      this.existingOrganization = null

      this.$http.post("/agencies/registrations/validate_agency", {
        name: this.organizationName,
        holding_company_id: this.holdingCompany
      }).then(result => {

      }, failure => {
        if (failure.body.code === "agency_exists") {
          this.agencyError = true
          this.existingOrganization = failure.body.organization
        }
      })
    },

    checkEmail() {
      this.emailError = null
      this.$http.post("/agencies/registrations/validate_email", {
        email: this.email
      }).then(result => {

      }, failure => {
        if (failure.body.code === "user_exists") {
          this.emailError = true
        }
      })
    }
  },

  computed: {
    didFinalizeForm() {
      return this.didJoin || this.didSignUp
    },

    validForCreate() {
      return !this.agencyError && !this.emailError
    },

    holdingCompaniesOptions() {
      if (!this.holdingCompanies) return []

      return this.holdingCompanies.map(el => {
        return {
          text: el.name,
          value: el.id
        }
      })
    },

    existingOrganizationName() {
      if (this.existingOrganization) {
        return this.existingOrganization.name
      }
      return null
    }
  }
}
</script>